import * as React from "react";

import type { AuditEventRow } from "@/hooks/queries/admin-audit-types";
import { formatDateTime } from "@/lib/format-date";

// Story 11.7 (AC9/AC10, contract rows 8-13) — the audit events table.
//
// Data-dense and read-only: radius none (design-system.md:329), no elevation, no
// alarm colouring on ordinary rows, horizontal scroll INSIDE the container so the
// page body never scrolls sideways.

const CELL = "px-4 py-3 align-top text-[color:var(--text-secondary)]";
const HEAD = "px-4 py-3 font-semibold";

export function AuditLogTable({ rows }: { rows: AuditEventRow[] }) {
  return (
    <div className="overflow-x-auto rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)]">
      <table className="w-full border-collapse text-left text-sm">
        <caption className="sr-only">
          Audit events, newest first
        </caption>
        <thead>
          <tr className="border-b border-[color:var(--bg-quaternary)] text-xs uppercase tracking-wide text-[color:var(--text-tertiary)]">
            <th scope="col" className={HEAD}>When</th>
            <th scope="col" className={HEAD}>Event</th>
            <th scope="col" className={HEAD}>Actor</th>
            <th scope="col" className={HEAD}>Organization</th>
            <th scope="col" className={HEAD}>Org role</th>
            <th scope="col" className={HEAD}>Version</th>
            <th scope="col" className={HEAD}>Payload</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.event_id}
              className="border-b border-[color:var(--bg-quaternary)] last:border-b-0"
            >
              <td className={`${CELL} whitespace-nowrap [font-variant-numeric:tabular-nums]`}>
                {formatDateTime(row.occurred_at)}
              </td>
              <td className={`${CELL} font-medium text-[color:var(--text-primary)]`}>
                {row.event_name}
              </td>
              <td className={CELL}>
                <ActorCell row={row} />
              </td>
              {/* tabular-nums: this cell falls back to a raw UUID, so its digits
                  must column-align down the table like When and Version do. */}
              <td className={`${CELL} [font-variant-numeric:tabular-nums]`}>
                {orgLabel(row)}
              </td>
              <td className={CELL}>{row.actor_org_role ?? "—"}</td>
              <td className={`${CELL} [font-variant-numeric:tabular-nums]`}>
                {row.event_version}
              </td>
              <td className={CELL}>
                <PayloadCell payload={row.payload} eventId={row.event_id} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * `actor: null` is a system/worker event — nobody caused it, so "System" is the
 * honest answer. An actor id that resolves to no projection row is a DIFFERENT
 * answer: someone caused it and we cannot name them, so the id is shown.
 *
 * After an account is erased (Story 11.12) the address here IS the projection's
 * tombstone — rendered verbatim, never the original (AC4).
 */
function ActorCell({ row }: { row: AuditEventRow }) {
  if (!row.actor) return <span>System</span>;
  const name = [row.actor.first_name, row.actor.last_name].filter(Boolean).join(" ");
  if (!row.actor.email) {
    return (
      <span className="flex flex-col">
        <span className="text-[color:var(--text-primary)]">Unknown actor</span>
        {/* The unresolved actor's id is the only thing naming them — tabular-nums so
            two near-identical UUIDs can be told apart by scanning the column. */}
        <span className="text-xs text-[color:var(--text-tertiary)] [font-variant-numeric:tabular-nums]">
          {row.actor.user_id}
        </span>
      </span>
    );
  }
  return (
    <span className="flex flex-col">
      <span className="text-[color:var(--text-primary)]">{row.actor.email}</span>
      {name ? <span className="text-xs text-[color:var(--text-tertiary)]">{name}</span> : null}
    </span>
  );
}

/** The recorded organization: its name when resolvable, else the raw id. Never fabricated. */
function orgLabel(row: AuditEventRow): string {
  if (!row.org) return "—";
  return row.org.org_name ?? row.org.org_id;
}

/**
 * Collapsed one-line preview plus a toggle to the full JSON. A per-row modal would
 * be hostile to scanning a forensic list, and always-expanded destroys row density.
 *
 * `refusal_count` is surfaced in the preview because it is the escalation signal a
 * reader is looking for. It is the THRESHOLD crossed, never an exact attempt total —
 * the emitter fires at 1 / 10 / 100 only, so the ABSENCE of a row above 100 in a
 * window is expected, not missing data.
 */
function PayloadCell({
  payload,
  eventId,
}: {
  payload: Record<string, unknown>;
  eventId: string;
}) {
  const [open, setOpen] = React.useState(false);
  const regionId = `audit-payload-${eventId}`;
  const json = JSON.stringify(payload ?? {}, null, 2);
  const refusalCount = payload?.refusal_count;

  return (
    <div className="flex flex-col gap-2">
      <span className="block max-w-[28rem] truncate text-xs text-[color:var(--text-tertiary)]">
        {refusalCount !== undefined && refusalCount !== null
          ? `refusal_count: ${String(refusalCount)}`
          : previewOf(payload)}
      </span>
      <button
        type="button"
        aria-expanded={open}
        // Only while the region exists: an aria-controls pointing at an id that
        // is not in the DOM is a dangling reference, and a screen reader
        // following it lands nowhere.
        aria-controls={open ? regionId : undefined}
        className="self-start rounded-sm text-xs font-medium text-[color:var(--text-brand-secondary)] underline focus:outline-none focus:ring-2 focus:ring-[color:var(--focus-ring)]"
        onClick={() => setOpen((v) => !v)}
      >
        {open ? "Hide payload" : "Show payload"}
      </button>
      {open ? (
        <pre
          id={regionId}
          className="max-w-[28rem] overflow-x-auto rounded-none border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] p-3 text-xs text-[color:var(--text-primary)]"
        >
          {json}
        </pre>
      ) : null}
    </div>
  );
}

/** One line of the payload's keys — enough to recognise a row without opening it. */
function previewOf(payload: Record<string, unknown>): string {
  const keys = Object.keys(payload ?? {});
  if (keys.length === 0) return "—";
  return keys.join(", ");
}
